import React from "react";
import HighlightPanel from "../../shared/HighlightPanel";
import MarketplaceListCard from "../../shared/MarketplaceListCard";
import PrimaryActionLink from "../../shared/PrimaryActionLink";
import StatusBadge from "../../shared/StatusBadge";
import { formatDateTime } from "../../../utils/date";
import {
  getCustomerRequestHighlightPanel,
  getOrderFulfillmentPolicy,
} from "../../../utils/marketplaceStatus";
import {
  getRichiestaExpiresAt,
  hasStartedProcess,
  isRichiestaExpired,
} from "../../../utils/richiestaExpiry";

const formatPrice = (value) => `${Number(value || 0).toFixed(2)} EUR`;
const getBestOfferSavings = (bestOffer, prodotti = []) => {
  if (!bestOffer) {
    return { savings: 0, percent: 0 };
  }

  const baseTotal = (Array.isArray(prodotti) ? prodotti : []).reduce(
    (total, prodotto) => total + Number(prodotto.prezzoIndicativo || 0) * Number(prodotto.quantita || 0),
    0
  );

  const lineSavings = Array.isArray(bestOffer?.voci)
    ? bestOffer.voci.reduce((total, voce) => total + Number(voce?.scontoValore || 0), 0)
    : 0;
  const explicitSavings = lineSavings + Number(bestOffer?.scontoOrdineValore || 0);
  const savings =
    explicitSavings > 0
      ? Number(explicitSavings.toFixed(2))
      : Math.max(0, Number((baseTotal - Number(bestOffer?.prezzoTotale || 0)).toFixed(2)));
  const percent = baseTotal > 0 ? Math.max(0, Math.round((savings / baseTotal) * 100)) : 0;

  return { savings, percent };
};

const CustomerRequestCard = ({
  richiesta,
  title,
  bestOffer,
  acceptedOffer,
  isPaid,
  isInProgress,
  isPaymentPending,
}) => {
  const offerte = Array.isArray(richiesta?.offerte) ? richiesta.offerte : [];
  const prodotti = Array.isArray(richiesta?.prodotti) ? richiesta.prodotti : [];
  const requestPath = `${process.env.PUBLIC_URL}/richieste/${richiesta.id || richiesta._id}`;
  const isExpired = isRichiestaExpired(richiesta);
  const processStarted = hasStartedProcess(richiesta);
  const expiresAt = getRichiestaExpiresAt(richiesta);
  const expiryMetaText =
    !processStarted && expiresAt
      ? `${isExpired ? "Scaduta" : "Scade"} il ${formatDateTime(expiresAt.toISOString())}`
      : "";
  const bestOfferSavings = getBestOfferSavings(bestOffer, prodotti);
  const acceptedOfferSavings = getBestOfferSavings(acceptedOffer, prodotti);
  const fulfillmentPolicy = isPaid || isInProgress ? getOrderFulfillmentPolicy(richiesta) : null;
  const highlightPanel = getCustomerRequestHighlightPanel({
    acceptedOffer,
    bestOffer,
    offerteCount: offerte.length,
    isPaid,
    isInProgress,
    isPaymentPending,
    fulfillmentPolicy,
    formatPrice,
    acceptedOfferSavings,
    bestOfferSavings,
  });

  return (
    <MarketplaceListCard
      className="customer-request-card"
      code={richiesta.codice}
      title={title}
      headerBadges={
        <StatusBadge tone={isExpired ? "danger" : "info"} className="customer-request-card__status">
          {isExpired ? "SCADUTA" : richiesta.stato}
        </StatusBadge>
      }
      metaItems={[
        { icon: "fa-shopping-basket", text: `${prodotti.length} prodotti` },
        { icon: "fa-clock-o", text: formatDateTime(richiesta.createdAt) },
        { icon: "fa-envelope-open-o", text: `${offerte.length} offerte` },
        ...(expiryMetaText
          ? [{ icon: "fa-hourglass-end", text: expiryMetaText, className: "marketplace-list-card__meta-item--danger" }]
          : []),
      ]}
      note={richiesta.noteUtente}
      items={prodotti.map((prodotto) => ({
        label: prodotto.nome,
        value: `x${prodotto.quantita}`,
      }))}
      actions={
        <PrimaryActionLink to={requestPath} className="customer-request-card__cta">
          Apri dettaglio richiesta
        </PrimaryActionLink>
      }
    >
      <HighlightPanel
        className={acceptedOffer || bestOffer ? "customer-request-card__offers-highlight" : "customer-request-card__pending-offers"}
        tone={highlightPanel.tone}
        eyebrow={highlightPanel.eyebrow}
        title={highlightPanel.title}
        description={highlightPanel.description}
        metrics={highlightPanel.metrics}
        icon={highlightPanel.icon}
      />
    </MarketplaceListCard>
  );
};

export default CustomerRequestCard;
